<template>
  <header class="header">
    <div class="header-left">
      <button class="hamburger-btn" @click.stop="toggleSidebar">
        <svg width="22" height="22" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round">
          <line x1="3" y1="6" x2="21" y2="6"></line>
          <line x1="3" y1="12" x2="21" y2="12"></line>
          <line x1="3" y1="18" x2="21" y2="18"></line>
        </svg>
      </button>
      <div class="breadcrumb">
        <span>AI 控制台</span>
        <span class="breadcrumb-separator">/</span>
        <span class="current">{{ navStore.breadcrumbTitle }}</span>
      </div>
      <div class="mobile-title">{{ navStore.breadcrumbTitle }}</div>
    </div>

    <div
      class="header-actions"
      style="display: flex; align-items: center; gap: 16px"
    >
      <div v-if="userStore.userInfo" class="notification-wrapper" @click.stop="notifOpen = !notifOpen">
        <svg class="notif-bell" width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
          <path d="M18 8A6 6 0 0 0 6 8c0 7-3 9-3 9h18s-3-2-3-9"/>
          <path d="M13.73 21a2 2 0 0 1-3.46 0"/>
        </svg>
        <span v-if="hasUnread" class="notif-dot"></span>
        <div v-if="notifOpen" class="notif-panel">
          <div class="notif-header">通知</div>
          <div class="notif-list">
            <div v-if="notifications.length === 0" class="notif-empty">暂无通知</div>
            <div v-for="item in notifications" :key="item.id" class="notif-item">
              <div class="notif-item-top">
                <span class="notif-item-title">{{ item.title }}</span>
                <span class="notif-item-date">{{ item.date }}</span>
              </div>
              <div class="notif-item-content">{{ item.content }}</div>
            </div>
          </div>
        </div>
      </div>

      <div v-if="userStore.userInfo" class="balance-badge">
        <span>算力: {{ userStore.userInfo.balance }}</span>
        <button
          class="balance-refresh-btn"
          :class="{ 'is-loading': refreshing }"
          :disabled="refreshing"
          title="刷新算力"
          @click.stop="refreshBalance"
        >
          <svg width="13" height="13" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2.5" stroke-linecap="round" stroke-linejoin="round">
            <polyline points="23 4 23 10 17 10"/>
            <polyline points="1 20 1 14 7 14"/>
            <path d="M3.51 9a9 9 0 0 1 14.85-3.36L23 10M1 14l4.64 4.36A9 9 0 0 0 20.49 15"/>
          </svg>
        </button>
      </div>

      <UserDropdown
        :open="userStore.headerMenuOpen"
        :user-name="userStore.userInfo?.username || userStore.userName"
        :avatar="avatar"
        @toggle="userStore.toggleHeaderMenu"
        @go-account="goAccountManage"
        @logout="handleLogout"
      />
    </div>
  </header>
</template>

<script setup>
import { inject, onMounted, onUnmounted, ref, reactive, computed } from "vue";
import { useRouter } from "vue-router";
import UserDropdown from "./UserDropdown.vue";
import { useNavStore } from "@/stores/useNavStore";
import { useAuthStore } from "@/stores/useAuthStore";
import { httpRequest } from "@/api/httpClient";

const navStore = useNavStore();
const userStore = useAuthStore();
const router = useRouter();
const avatar = "/默认头像.svg";

const toggleSidebar = inject("toggleSidebar");
const notifOpen = ref(false);
const refreshing = ref(false);

const refreshBalance = async () => {
  if (refreshing.value) return;
  refreshing.value = true;
  try {
    await userStore.refreshProfileSilently();
  } finally {
    refreshing.value = false;
  }
};

const NOTIF_READ_KEY = 'zp_notif_read_date';
const notifications = reactive([]);

const loadNotifications = async () => {
  try {
    const data = await httpRequest('/api/notifications');
    const entries = data?.entries || [];
    notifications.splice(0, notifications.length, ...entries);
  } catch (e) {
    // ignore
  }
};

const hasUnread = computed(() => {
  const readDate = localStorage.getItem(NOTIF_READ_KEY);
  if (!readDate) return notifications.length > 0;
  return notifications.length > 0 && notifications[0].date > readDate;
});

const goAccountManage = () => {
  userStore.closeHeaderMenu();
  router.push("/account");
};

const handleLogout = async () => {
  userStore.closeHeaderMenu();
  try {
    await userStore.logout();
  } finally {
    router.push("/login");
  }
};

const onWindowClick = () => {
  userStore.closeHeaderMenu();
  if (notifOpen.value) {
    if (hasUnread.value && notifications.length > 0) {
      localStorage.setItem(NOTIF_READ_KEY, notifications[0].date);
    }
    notifOpen.value = false;
  }
};

onMounted(() => {
  window.addEventListener("click", onWindowClick);
  loadNotifications();
});

onUnmounted(() => {
  window.removeEventListener("click", onWindowClick);
});
</script>

<style scoped>
.balance-badge {
  display: flex;
  align-items: center;
  gap: 6px;
  padding: 4px 8px 4px 12px;
  background-color: #eaf2ff;
  color: #165dff;
  border-radius: 999px;
  font-size: 14px;
  font-weight: bold;
  border: 1px solid #bdd4ff;
}
.balance-refresh-btn {
  display: inline-flex;
  align-items: center;
  justify-content: center;
  width: 20px;
  height: 20px;
  padding: 0;
  border: none;
  background: transparent;
  color: #165dff;
  border-radius: 50%;
  cursor: pointer;
  transition: background 0.15s;
}
.balance-refresh-btn:hover:not(:disabled) {
  background: rgba(22, 93, 255, 0.15);
}
.balance-refresh-btn:disabled {
  cursor: progress;
  opacity: 0.6;
}
.balance-refresh-btn.is-loading svg {
  animation: balance-refresh-spin 0.8s linear infinite;
}
@keyframes balance-refresh-spin {
  to { transform: rotate(360deg); }
}
.header-left {
  display: flex;
  align-items: center;
  gap: 8px;
}
.hamburger-btn {
  display: none;
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  color: var(--text-title);
}
.mobile-title {
  display: none;
  font-size: 16px;
  font-weight: 600;
}
.notification-wrapper {
  position: relative;
  cursor: pointer;
  padding: 6px;
  border-radius: 8px;
  transition: background 0.15s;
}
.notification-wrapper:hover {
  background: #f0f5ff;
}
.notif-bell {
  display: block;
  color: #555;
}
.notif-dot {
  position: absolute;
  top: 4px;
  right: 4px;
  width: 8px;
  height: 8px;
  background: #f53f3f;
  border-radius: 50%;
  border: 2px solid #fff;
}
.notif-panel {
  position: absolute;
  right: 0;
  top: calc(100% + 8px);
  width: 340px;
  background: #fff;
  border-radius: 10px;
  box-shadow: 0 10px 30px rgba(0, 0, 0, 0.08);
  border: 1px solid #e8e8e8;
  z-index: 2200;
  overflow: hidden;
}
.notif-header {
  padding: 12px 16px;
  font-size: 15px;
  font-weight: 600;
  color: #1d2129;
  border-bottom: 1px solid #f0f0f0;
}
.notif-list {
  max-height: 320px;
  overflow-y: auto;
}
.notif-item {
  padding: 12px 16px;
  border-bottom: 1px solid #f7f7f7;
}
.notif-item:last-child {
  border-bottom: none;
}
.notif-item-top {
  display: flex;
  justify-content: space-between;
  align-items: center;
  margin-bottom: 4px;
}
.notif-item-title {
  font-size: 13px;
  font-weight: 600;
  color: #1d2129;
}
.notif-item-date {
  font-size: 12px;
  color: #c0c0c0;
}
.notif-item-content {
  font-size: 13px;
  color: #666;
  line-height: 1.5;
}
.notif-empty {
  padding: 24px 16px;
  text-align: center;
  color: #c0c0c0;
  font-size: 13px;
}
@media (max-width: 768px) {
  .hamburger-btn {
    display: flex;
    align-items: center;
  }
  .breadcrumb {
    display: none;
  }
  .mobile-title {
    display: block;
  }
  .balance-badge {
    display: none;
  }
  .notif-panel {
    width: 280px;
    right: -40px;
  }
}
</style>
